/**
 * Turning a failed artifact fetch into something an operator can act on.
 *
 * The Storage SDK only maps 401/402/403/404 to a named code; every other status
 * becomes `storage/unknown` — "An unknown error occurred, please check the error
 * payload for server response" — with the payload itself hidden on
 * `serverResponse`. A bucket that is not registered with Firebase Storage answers
 * 400 with exactly the console link that fixes it, so dropping that text is the
 * difference between a diagnosable error and a dead end.
 */
import { StorageError, StorageErrorCode } from 'firebase/storage';

/**
 * `retry-limit-exceeded` means the SDK spent its whole retry budget treating the
 * reads as network failures, which is also what it sees when the object comes
 * back fine but the browser discards the response for want of an
 * `Access-Control-Allow-Origin` header. Firebase sets that header on preflights
 * and errors, so a bucket missing the `cors` block fails in exactly this shape
 * and nowhere else — worth naming, since the code itself points at the network.
 */
const RETRY_LIMIT_HINT =
    'The reads either never reached Firebase, or arrived without CORS headers — check the artifacts bucket has a cors config (automation/deployer/infra/storage.ts).';

export function storageErrorText(err: unknown): string {
    if (!(err instanceof StorageError)) return String(err);
    if (err.serverResponse) return `${String(err)} — ${err.serverResponse}`;
    if (err.code === `storage/${StorageErrorCode.RETRY_LIMIT_EXCEEDED}`) return `${String(err)} — ${RETRY_LIMIT_HINT}`;
    return String(err);
}
